// ============================================================
// src/pages/AllTimesheetsPage.jsx  [Supervisor]
// No extra packages needed — custom calendar built from scratch
// ============================================================
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getAllTimesheets, getKPIs, getWeekEntries, reviewTimesheet, addManualEntry, exportTimesheets } from '../api/timesheetApi';
import { getEmployees } from '../api/employeeApi';
import { Toast, Btn, Field, Input, Select, Modal, StatusBadge, Spinner } from '../components/UI';
import { useToast } from '../hooks/useToast';

// ── Date helpers (all state in "mm-dd-yyyy") ─────────────────
function toMMDDYYYY(d) {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${mm}-${dd}-${d.getFullYear()}`;
}
function getCurrentWeekEnding() {
  const today = new Date();
  const sat = new Date(today);
  sat.setDate(today.getDate() + (6 - today.getDay()));
  return toMMDDYYYY(sat);
}
function mdyToDate(mdy) {
  if (!mdy || !/^\d{2}-\d{2}-\d{4}$/.test(mdy)) return null;
  const [m, d, y] = mdy.split('-');
  const dt = new Date(+y, +m - 1, +d);
  return isNaN(dt.getTime()) ? null : dt;
}
function dateToMdy(dt) {
  return dt ? toMMDDYYYY(dt) : '';
}

// ── Calendar constants ───────────────────────────────────────
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

// ── Custom DatePicker (zero deps, position:fixed popup) ──────
function DatePicker({ value, onChange, small = false }) {
  const selected = mdyToDate(value);
  const today = new Date();
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState((selected || today).getFullYear());
  const [viewMonth, setViewMonth] = useState((selected || today).getMonth());
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handle = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  useEffect(() => {
    if (selected) { setViewYear(selected.getFullYear()); setViewMonth(selected.getMonth()); }
  }, [value]);

  const prevMonth = () => viewMonth === 0 ? (setViewMonth(11), setViewYear(y => y - 1)) : setViewMonth(m => m - 1);
  const nextMonth = () => viewMonth === 11 ? (setViewMonth(0), setViewYear(y => y + 1)) : setViewMonth(m => m + 1);

  function buildDays() {
    const first = new Date(viewYear, viewMonth, 1).getDay();
    const total = new Date(viewYear, viewMonth + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < first; i++) cells.push(null);
    for (let d = 1; d <= total; d++) cells.push(d);
    return cells;
  }

  const isSelected = d => d && selected &&
    selected.getFullYear() === viewYear && selected.getMonth() === viewMonth && selected.getDate() === d;
  const isToday = d => d &&
    today.getFullYear() === viewYear && today.getMonth() === viewMonth && today.getDate() === d;

  function selectDay(day) {
    if (!day) return;
    onChange(dateToMdy(new Date(viewYear, viewMonth, day)));
    setOpen(false);
  }

  const years = [];
  for (let y = today.getFullYear() - 5; y <= today.getFullYear() + 5; y++) years.push(y);

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: '100%' }}>
      {/* Trigger */}
      <div style={{ position: 'relative', width: '100%' }}>
        <input
          type="text" readOnly
          value={value || ''}
          placeholder="mm-dd-yyyy"
          onClick={() => setOpen(o => !o)}
          style={{
            width: '100%', boxSizing: 'border-box',
            border: `1.5px solid ${open ? '#dc2626' : '#d1d5db'}`,
            borderRadius: '6px',
            padding: small ? '0.28rem 2.2rem 0.28rem 0.55rem' : '0.48rem 2.4rem 0.48rem 0.75rem',
            fontSize: small ? '0.8rem' : '0.875rem',
            color: value ? '#111827' : '#9ca3af',
            background: '#fff', outline: 'none', cursor: 'pointer',
            transition: 'border-color 0.15s',
          }}
        />
        <span onClick={() => setOpen(o => !o)} style={{
          position: 'absolute', right: '0.55rem', top: '50%',
          transform: 'translateY(-50%)',
          fontSize: small ? '0.9rem' : '1rem',
          cursor: 'pointer', userSelect: 'none', lineHeight: 1,
        }}>📅</span>
      </div>

      {/* Popup — fixed, auto-flips up/down */}
      {open && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0,
            zIndex: 99999,
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
            width: '288px',
            userSelect: 'none',
          }}
          ref={el => {
            if (!el || !wrapRef.current) return;
            const rect = wrapRef.current.getBoundingClientRect();
            const popH = el.offsetHeight || 300;
            const below = window.innerHeight - rect.bottom;
            el.style.top = below >= popH + 8 || below >= rect.top
              ? `${rect.bottom + 6}px`
              : `${rect.top - popH - 6}px`;
            let left = rect.left;
            if (left + 288 > window.innerWidth - 8) left = window.innerWidth - 296;
            if (left < 8) left = 8;
            el.style.left = `${left}px`;
          }}
        >
          {/* Header */}
          <div style={{ padding: '12px 14px 8px', borderBottom: '1px solid #f3f4f6' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
              <button onClick={prevMonth} style={navBtn}>‹</button>
              <div style={{ display: 'flex', gap: '6px' }}>
                <select value={viewMonth} onChange={e => setViewMonth(+e.target.value)} style={dropStyle}>
                  {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
                </select>
                <select value={viewYear} onChange={e => setViewYear(+e.target.value)} style={dropStyle}>
                  {years.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <button onClick={nextMonth} style={navBtn}>›</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '2px' }}>
              {DAYS.map(d => (
                <div key={d} style={{ textAlign: 'center', fontSize: '0.62rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', lineHeight: '26px' }}>{d}</div>
              ))}
            </div>
          </div>

          {/* Days */}
          <div style={{ padding: '8px 10px 12px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '2px' }}>
              {buildDays().map((day, i) => (
                <div key={i} onClick={() => selectDay(day)} style={{
                  height: '34px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: '7px', fontSize: '0.82rem',
                  cursor: day ? 'pointer' : 'default',
                  fontWeight: isSelected(day) || isToday(day) ? 700 : 400,
                  background: isSelected(day) ? '#dc2626' : 'transparent',
                  color: isSelected(day) ? '#fff' : isToday(day) ? '#dc2626' : day ? '#374151' : 'transparent',
                  transition: 'background 0.1s, color 0.1s',
                }}
                  onMouseEnter={e => { if (day && !isSelected(day)) { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.color = '#dc2626'; } }}
                  onMouseLeave={e => { if (day && !isSelected(day)) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = isToday(day) ? '#dc2626' : '#374151'; } }}
                >{day || ''}</div>
              ))}
            </div>
            {/* Today shortcut */}
            <div style={{ textAlign: 'center', marginTop: '8px', borderTop: '1px solid #f3f4f6', paddingTop: '8px' }}>
              <button onClick={() => { onChange(dateToMdy(today)); setOpen(false); }} style={{
                background: 'none', border: '1px solid #e5e7eb', borderRadius: '5px',
                padding: '3px 14px', fontSize: '0.75rem', fontWeight: 600,
                color: '#374151', cursor: 'pointer',
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#dc2626'; e.currentTarget.style.color = '#dc2626'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.color = '#374151'; }}
              >Today</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const navBtn = { background: 'none', border: '1px solid #e5e7eb', borderRadius: '5px', width: '28px', height: '28px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', color: '#6b7280', lineHeight: 1 };
const dropStyle = { fontSize: '0.78rem', fontWeight: 600, border: '1px solid #e5e7eb', borderRadius: '5px', padding: '3px 6px', color: '#374151', background: '#f9fafb', outline: 'none', cursor: 'pointer' };

// ── Stat Card ────────────────────────────────────────────────
function StatCard({ label, value, sub, color = 'gray' }) {
  const borders = { gray: '#9ca3af', green: '#16a34a', yellow: '#d97706', red: '#dc2626' };
  const values = { gray: '#111827', green: '#16a34a', yellow: '#d97706', red: '#dc2626' };
  return (
    <div style={{
      background: '#fff', border: '1px solid #e5e7eb',
      borderTop: `3px solid ${borders[color]}`,
      borderRadius: '6px', padding: '1rem 1.25rem',
      boxShadow: '0 1px 3px rgba(0,0,0,.06)', minWidth: '140px',
    }}>
      <div style={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '.06em', color: '#6b7280', textTransform: 'uppercase', marginBottom: '0.35rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {label}
      </div>
      <div style={{ fontSize: '2rem', fontWeight: 700, color: values[color], lineHeight: 1.1, marginBottom: '0.25rem' }}>
        {value}
      </div>
      <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{sub}</div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
export default function AllTimesheetsPage() {
  const { toast, showToast, clearToast } = useToast();
  const [rows, setRows] = useState([]);
  const [kpis, setKpis] = useState({});
  const [loading, setLoading] = useState(true);
  const [mechanics, setMechanics] = useState([]);
  const [filters, setFilters] = useState({
    week_ending: getCurrentWeekEnding(), status: '', search: '',
  });

  const [reviewTarget, setReviewTarget] = useState(null);
  const [reviewAction, setReviewAction] = useState('APPROVE');
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewing, setReviewing] = useState(false);

  const [detailWeek, setDetailWeek] = useState(null);
  const [detailEntries, setDetailEntries] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [manualOpen, setManualOpen] = useState(false);
  const [manualForm, setManualForm] = useState({
    employee_id: '',
    entry_date: getCurrentWeekEnding(),   // stored as mm-dd-yyyy
    work_order: '', job_number: '', description: '',
    time_in: '07:00', time_out: '15:30', hours_worked: '',
  });
  const [savingManual, setSavingManual] = useState(false);
  const mUpd = k => e => setManualForm(f => ({ ...f, [k]: e.target.value }));

  // Auto-calc hours
  useEffect(() => {
    const [h1, m1] = manualForm.time_in.split(':').map(Number);
    const [h2, m2] = manualForm.time_out.split(':').map(Number);
    if (isNaN(h1) || isNaN(h2)) return;
    let mins = (h2 * 60 + m2) - (h1 * 60 + m1);
    if (mins < 0) mins += 1440;
    const hrs = Math.round((mins / 60) * 2) / 2;
    setManualForm(f => ({ ...f, hours_worked: hrs > 0 ? String(hrs) : '' }));
  }, [manualForm.time_in, manualForm.time_out]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [tsData, kpiData] = await Promise.all([
        getAllTimesheets(filters),
        getKPIs(filters.week_ending),
      ]);
      setRows(tsData);
      setKpis(kpiData);
    } catch (err) {
      showToast(err.message || 'Failed to load', 'error');
    } finally {
      setLoading(false);
    }
  }, [filters, showToast]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { getEmployees().then(setMechanics).catch(() => { }); }, []);

  // KPI derived
  const totalMechanics = Number(kpis.total_mechanics ?? 0);
  const approved = Number(kpis.approved ?? 0);
  const notSubmitted = Number(kpis.not_submitted ?? 0);
  const totalHours = parseFloat(kpis.total_hours || 0);
  const totalOvertime = parseFloat(kpis.total_overtime || 0);
  const pendingReview = Number(kpis.pending_review ?? 0);
  const completionPct = totalMechanics > 0 ? Math.round((approved / totalMechanics) * 100) : 0;
  const avgHrs = totalMechanics > 0 ? (totalHours / totalMechanics).toFixed(1) : '0.0';
  const otPct = totalHours > 0 ? ((totalOvertime / totalHours) * 100).toFixed(1) : '0.0';

  // function handleExport() {
  //   const params = new URLSearchParams();
  //   if (filters.week_ending) params.set('week_ending', filters.week_ending);
  //   if (filters.status)      params.set('status',      filters.status);
  //   if (filters.search)      params.set('search',      filters.search);
  //   window.open(`/api/timesheets/export?${params.toString()}`, '_blank');
  // }

  async function handleExport() {
    try {
      await exportTimesheets(filters.week_ending, filters.status, filters.search);
      showToast('Excel downloaded ✓', 'success');
    } catch (err) {
      showToast(err.message || 'Export failed', 'error');
    }
  }

  async function handleReview() {
    setReviewing(true);
    try {
      await reviewTimesheet(reviewTarget.id, reviewAction, reviewNotes);
      showToast(`Timesheet ${reviewAction.toLowerCase()}d ✓`, 'success');
      setReviewTarget(null); setReviewNotes('');
      fetchData();
    } catch (err) {
      showToast(err.message || 'Review failed', 'error');
    } finally {
      setReviewing(false);
    }
  }

  async function openDetail(week) {
    setDetailWeek(week); setLoadingDetail(true);
    try { setDetailEntries(await getWeekEntries(week.id)); }
    catch { setDetailEntries([]); }
    finally { setLoadingDetail(false); }
  }

  async function handleManualEntry(e) {
    e.preventDefault();
    if (!manualForm.employee_id || !manualForm.job_number.trim() || !manualForm.description.trim()) {
      showToast('Employee, Job # and Description required', 'error'); return;
    }
    setSavingManual(true);
    try {
      await addManualEntry({
        employee_id: manualForm.employee_id,
        entry_date: manualForm.entry_date,   // already mm-dd-yyyy
        work_order: manualForm.work_order.trim(),
        job_number: manualForm.job_number.trim(),
        description: manualForm.description.trim(),
        time_in: manualForm.time_in,
        time_out: manualForm.time_out,
        hours_worked: manualForm.hours_worked,
      });
      showToast('Manual entry added ✓', 'success');
      setManualOpen(false);
      setManualForm(f => ({ ...f, work_order: '', job_number: '', description: '', employee_id: '' }));
      fetchData();
    } catch (err) {
      showToast(err.message || 'Failed to add', 'error');
    } finally {
      setSavingManual(false);
    }
  }

  const fUpd = k => e => setFilters(f => ({ ...f, [k]: e.target.value }));

  const inpStyle = {
    border: '1px solid #d1d5db', borderRadius: '4px',
    padding: '0.45rem 0.65rem', fontSize: '0.875rem',
    outline: 'none', background: '#fff', color: '#111827',
    width: '100%', boxSizing: 'border-box',
  };

  return (
    <div style={{ padding: '1.5rem', background: '#f3f4f6', minHeight: '100vh' }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ fontSize: '1.6rem', fontWeight: 800, margin: 0, letterSpacing: '.02em', color: '#111827' }}>
            ALL TIMESHEETS
          </h1>
          <p style={{ margin: '0.2rem 0 0', fontSize: '0.85rem', color: '#6b7280' }}>
            Real-time view — Week Ending{' '}
            <span style={{ color: '#dc2626', fontWeight: 600 }}>{filters.week_ending || '—'}</span>
          </p>
        </div>
        <button onClick={handleExport} style={{
          background: '#dc2626', color: '#fff', border: 'none',
          padding: '0.6rem 1.25rem', borderRadius: '5px',
          fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: '0.4rem',
          boxShadow: '0 1px 3px rgba(0,0,0,.15)',
        }}>⬇ EXPORT EXCEL</button>
      </div>

      {/* ── KPI Cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <StatCard label="Total Mechanics" value={totalMechanics} sub="Active this week" color="gray" />
        <StatCard label="Approved" value={approved} sub={`${completionPct}% completion`} color="green" />
        <StatCard label="Pending Review" value={pendingReview} sub="Awaiting approval" color="yellow" />
        <StatCard label="Not Submitted" value={notSubmitted} sub="Follow-up required" color="red" />
        <StatCard label="Total Hours (Week)" value={totalHours.toFixed(0)} sub={`Avg ${avgHrs} hrs/person`} color="gray" />
        <StatCard label="Overtime Hours" value={totalOvertime.toFixed(0)} sub={`${otPct}% of total`} color={totalOvertime > 0 ? 'red' : 'gray'} />
      </div>

      {/* ── Filter Row ── */}
      <div style={{
        background: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px',
        padding: '1rem 1.25rem', marginBottom: '1.25rem',
        display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap',
      }}>
        {/* Search */}
        <div style={{ flex: 1, minWidth: '180px' }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: '0.3rem' }}>Search</div>
          <input type="text" style={inpStyle} value={filters.search} onChange={fUpd('search')}
            placeholder="Name or EE#…" onKeyDown={e => e.key === 'Enter' && fetchData()} />
        </div>

        {/* Status */}
        <div style={{ minWidth: '140px' }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: '0.3rem' }}>Status</div>
          <select style={{ ...inpStyle, width: 'auto', minWidth: '140px' }} value={filters.status} onChange={fUpd('status')}>
            <option value="">All Status</option>
            <option value="draft">Draft</option>
            <option value="submitted">Submitted</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        {/* Week Ending — DatePicker */}
        <div style={{ minWidth: '160px' }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: '0.3rem' }}>Week Ending</div>
          <DatePicker
            value={filters.week_ending}
            onChange={val => setFilters(f => ({ ...f, week_ending: val }))}
          />
        </div>

        <button onClick={fetchData} style={{
          background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db',
          padding: '0.5rem 1rem', borderRadius: '4px',
          fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: '0.3rem', whiteSpace: 'nowrap',
        }}>🔍 FILTER</button>

        <button onClick={() => setManualOpen(true)} style={{
          background: '#dc2626', color: '#fff', border: 'none',
          padding: '0.5rem 1rem', borderRadius: '4px',
          fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: '0.3rem', whiteSpace: 'nowrap',
        }}>+ ADD ENTRY</button>
      </div>

      {/* ── Table ── */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px', boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}>
        <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid #e5e7eb', fontWeight: 700, fontSize: '0.9rem', color: '#374151' }}>
          Timesheet Records
        </div>
        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center' }}><Spinner /></div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  {['EE#', 'Last Name', 'First Name', 'Regular Hrs', 'OT Hrs', 'Total', 'Entries', 'Status', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '0.65rem 1rem', textAlign: 'left', fontSize: '0.72rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.04em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={9} style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af' }}>No timesheets found.</td></tr>
                ) : rows.map(r => (
                  <tr key={`${r.employee_id}-${r.id || 'none'}`}
                    style={{ borderBottom: '1px solid #f3f4f6' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                    onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                    <td style={{ padding: '0.7rem 1rem', color: '#dc2626', fontFamily: 'monospace', fontWeight: 600 }}>{r.ee_number}</td>
                    <td style={{ padding: '0.7rem 1rem', fontWeight: 600 }}>{r.last_name}</td>
                    <td style={{ padding: '0.7rem 1rem' }}>{r.first_name}</td>
                    <td style={{ padding: '0.7rem 1rem' }}>
                      {r.total_hours != null
                        ? <strong>{(parseFloat(r.total_hours) - parseFloat(r.overtime_hours || 0)).toFixed(1)}</strong>
                        : <span style={{ color: '#9ca3af' }}>—</span>}
                    </td>
                    <td style={{ padding: '0.7rem 1rem' }}>
                      {r.overtime_hours > 0
                        ? <span style={{ color: '#dc2626', fontWeight: 600 }}>{parseFloat(r.overtime_hours).toFixed(1)}</span>
                        : <span style={{ color: '#9ca3af' }}>—</span>}
                    </td>
                    <td style={{ padding: '0.7rem 1rem' }}>
                      {r.total_hours != null
                        ? <strong style={{ color: r.overtime_hours > 0 ? '#dc2626' : '#111827' }}>{parseFloat(r.total_hours).toFixed(1)}</strong>
                        : <span style={{ color: '#9ca3af' }}>—</span>}
                    </td>
                    <td style={{ padding: '0.7rem 1rem', color: '#6b7280', fontSize: '0.82rem' }}>
                      {r.entry_count > 0 ? <span>{r.entry_count} entries</span> : <span style={{ color: '#9ca3af' }}>—</span>}
                    </td>
                    <td style={{ padding: '0.7rem 1rem' }}><StatusBadge status={r.status || 'not_submitted'} /></td>
                    <td style={{ padding: '0.7rem 1rem' }}>
                      <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'nowrap' }}>
                        {r.id && (
                          <button onClick={() => openDetail(r)} style={{ padding: '0.25rem 0.6rem', fontSize: '0.75rem', fontWeight: 600, border: '1px solid #d1d5db', borderRadius: '4px', background: '#fff', color: '#374151', cursor: 'pointer' }}>VIEW</button>
                        )}
                        {r.status === 'submitted' && (<>
                          <button onClick={() => { setReviewTarget(r); setReviewAction('APPROVE'); setReviewNotes(''); }} style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', fontWeight: 600, border: '1px solid #16a34a', borderRadius: '4px', background: '#f0fdf4', color: '#16a34a', cursor: 'pointer' }}>✓</button>
                          <button onClick={() => { setReviewTarget(r); setReviewAction('REJECT'); setReviewNotes(''); }} style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', fontWeight: 600, border: '1px solid #dc2626', borderRadius: '4px', background: '#fef2f2', color: '#dc2626', cursor: 'pointer' }}>✗</button>
                        </>)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Review Modal ── */}
      <Modal open={!!reviewTarget}
        title={`${reviewAction === 'APPROVE' ? '✅ Approve' : '❌ Reject'} Timesheet`}
        onClose={() => setReviewTarget(null)}
        footer={<>
          <Btn variant="secondary" onClick={() => setReviewTarget(null)}>CANCEL</Btn>
          <Btn variant={reviewAction === 'APPROVE' ? 'success' : 'danger'} onClick={handleReview} disabled={reviewing}>
            {reviewing ? 'SAVING…' : reviewAction}
          </Btn>
        </>}>
        {reviewTarget && (
          <div>
            <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '0.75rem 1rem', marginBottom: '1rem' }}>
              {[['Mechanic', `${reviewTarget.last_name}, ${reviewTarget.first_name}`],
              ['EE#', reviewTarget.ee_number],
              ['Week', reviewTarget.week_ending_date],
              ['Total Hours', `${parseFloat(reviewTarget.total_hours || 0).toFixed(1)} hrs`],
              ].map(([l, v]) => (
                <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.3rem 0', fontSize: '0.875rem', borderBottom: '1px solid #f3f4f6' }}>
                  <span style={{ color: '#6b7280' }}>{l}</span>
                  <span style={{ fontWeight: 600 }}>{v}</span>
                </div>
              ))}
            </div>
            <Field label="Reviewer Notes">
              <textarea className="textarea" value={reviewNotes} onChange={e => setReviewNotes(e.target.value)} rows={3}
                placeholder={reviewAction === 'REJECT' ? 'Reason for rejection…' : 'Optional notes…'} />
            </Field>
          </div>
        )}
      </Modal>

      {/* ── Detail Modal ── */}
      <Modal open={!!detailWeek}
        title={`Entries: ${detailWeek?.last_name}, ${detailWeek?.first_name} – ${detailWeek?.week_ending_date}`}
        onClose={() => setDetailWeek(null)} wide>
        {loadingDetail ? <Spinner /> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  {['Date', 'WO#', 'Job#', 'Description', 'In', 'Out', 'Hrs', 'Entered By'].map(h => (
                    <th key={h} style={{ padding: '0.6rem 0.75rem', textAlign: 'left', fontSize: '0.72rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', borderBottom: '1px solid #e5e7eb' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {detailEntries.length === 0
                  ? <tr><td colSpan={8} style={{ padding: '1.5rem', textAlign: 'center', color: '#9ca3af' }}>No entries.</td></tr>
                  : detailEntries.map(en => (
                    <tr key={en.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '0.6rem 0.75rem', fontFamily: 'monospace' }}>{en.entry_date}</td>
                      <td style={{ padding: '0.6rem 0.75rem', fontFamily: 'monospace', color: '#7c3aed' }}>{en.work_order || '—'}</td>
                      <td style={{ padding: '0.6rem 0.75rem', fontFamily: 'monospace', color: '#dc2626' }}>{en.job_number}</td>
                      <td style={{ padding: '0.6rem 0.75rem', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{en.description}</td>
                      <td style={{ padding: '0.6rem 0.75rem', fontFamily: 'monospace', color: '#16a34a' }}>{en.time_in?.slice(0, 5)}</td>
                      <td style={{ padding: '0.6rem 0.75rem', fontFamily: 'monospace', color: '#dc2626' }}>{en.time_out?.slice(0, 5)}</td>
                      <td style={{ padding: '0.6rem 0.75rem' }}><strong>{parseFloat(en.hours_worked).toFixed(1)}h</strong></td>
                      <td style={{ padding: '0.6rem 0.75rem', color: '#9ca3af' }}>{en.entered_by || '—'}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </Modal>

      {/* ── Manual Entry Modal ── */}
      <Modal open={manualOpen} title="Add Manual Entry" onClose={() => setManualOpen(false)}
        footer={<>
          <Btn variant="secondary" onClick={() => setManualOpen(false)}>CANCEL</Btn>
          <Btn onClick={handleManualEntry} disabled={savingManual}>{savingManual ? 'SAVING…' : 'ADD ENTRY'}</Btn>
        </>}>
        <form onSubmit={handleManualEntry}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <Field label="Mechanic" required>
              <Select value={manualForm.employee_id} onChange={mUpd('employee_id')} required>
                <option value="">— Select Mechanic —</option>
                {mechanics.map(m => (
                  <option key={m.id} value={m.id}>{m.ee_number} – {m.last_name}, {m.first_name}</option>
                ))}
              </Select>
            </Field>

            {/* Date field — custom DatePicker */}
            <Field label="Date" required>
              <DatePicker
                value={manualForm.entry_date}
                onChange={val => setManualForm(f => ({ ...f, entry_date: val }))}
              />
            </Field>

            <Field label="WO #">
              <Input type="text" value={manualForm.work_order} onChange={mUpd('work_order')} placeholder="WO-1021" />
            </Field>
            <Field label="Job #" required>
              <Input type="text" value={manualForm.job_number} onChange={mUpd('job_number')} placeholder="JOB-2841" required />
            </Field>
            <Field label="Time In" required>
              <Input type="time" value={manualForm.time_in} onChange={mUpd('time_in')} required />
            </Field>
            <Field label="Time Out" required>
              <Input type="time" value={manualForm.time_out} onChange={mUpd('time_out')} required />
            </Field>
            <Field label={`Hours${manualForm.hours_worked ? ` (${manualForm.hours_worked})` : ''}`}>
              <Input type="number" value={manualForm.hours_worked} readOnly placeholder="Auto" />
            </Field>
            <Field label="Description" required>
              <Input type="text" value={manualForm.description} onChange={mUpd('description')} placeholder="Work description…" required />
            </Field>
          </div>
        </form>
      </Modal>

      <Toast message={toast.message} type={toast.type} onClose={clearToast} />
    </div>
  );
}